import { useState, useEffect } from 'react'
import FormModal from '../FormModal'
import { parseSteps } from '../QuestSteps'

const formStyles = {
  field: {
    marginBottom: '0.4rem',
  },
  label: {
    display: 'block',
    color: 'var(--text-primary)',
    marginBottom: '0.4rem',
    fontSize: '0.95rem',
    fontWeight: 500,
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    background: 'var(--accent-deep)',
    border: '1px solid var(--accent-teal)',
    borderRadius: '6px',
    color: 'var(--text-primary)',
    fontSize: '0.95rem',
    fontFamily: 'inherit',
  },
  textarea: {
    width: '100%',
    padding: '0.75rem',
    background: 'var(--accent-deep)',
    border: '1px solid var(--accent-teal)',
    borderRadius: '6px',
    color: 'var(--text-primary)',
    fontSize: '0.95rem',
    fontFamily: 'inherit',
    minHeight: '120px',
    resize: 'vertical',
  },
  select: {
    width: '100%',
    padding: '0.75rem',
    background: 'var(--accent-deep)',
    border: '1px solid var(--accent-teal)',
    borderRadius: '6px',
    color: 'var(--text-primary)',
    fontSize: '0.95rem',
    fontFamily: 'inherit',
    cursor: 'pointer',
  },
  option: {
    background: 'var(--accent-deep)',
    color: 'var(--text-primary)',
  },
  checkbox: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  checkboxInput: {
    width: '20px',
    height: '20px',
    cursor: 'pointer',
  },
}

function QuestForm({ isOpen, onClose, quest = null, onSubmit }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    full_description: '',
    quest_type: 'main',
    priority: 'normal',
    completed: 0,
    is_visible: 0,
    steps: [],
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Update form data when quest prop changes
  useEffect(() => {
    if (quest) {
      setFormData({
        title: quest.title || '',
        description: quest.description || '',
        full_description: quest.full_description || '',
        quest_type: quest.quest_type || 'main',
        priority: quest.priority || 'normal',
        completed: quest.completed || 0,
        is_visible: quest.is_visible || 0,
        steps: parseSteps(quest.steps),
      })
    } else {
      setFormData({
        title: '',
        description: '',
        full_description: '',
        quest_type: 'main',
        priority: 'normal',
        completed: 0,
        is_visible: 0,
        steps: [],
      })
    }
  }, [quest])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }))
  }

  const updateStep = (i, patch) => {
    setFormData(prev => ({ ...prev, steps: prev.steps.map((s, idx) => (idx === i ? { ...s, ...patch } : s)) }))
  }

  const addStep = () => {
    setFormData(prev => ({ ...prev, steps: [...prev.steps, { text: '', status: 'hidden' }] }))
  }

  const removeStep = (i) => {
    setFormData(prev => ({ ...prev, steps: prev.steps.filter((_, idx) => idx !== i) }))
  }

  const moveStep = (i, dir) => {
    setFormData(prev => {
      const arr = [...prev.steps]
      const j = i + dir
      if (j < 0 || j >= arr.length) return prev
      ;[arr[i], arr[j]] = [arr[j], arr[i]]
      return { ...prev, steps: arr }
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Drop empty step rows, then serialize for storage.
      const cleanedSteps = formData.steps.filter(s => s.text.trim() !== '')
      await onSubmit({ ...formData, steps: JSON.stringify(cleanedSteps) })
      onClose()
    } catch (error) {
      console.error('Form submission error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={quest ? 'Edit Quest' : 'Create New Quest'}
      isSubmitting={isSubmitting}
    >
      <div style={formStyles.field}>
        <label style={formStyles.label} htmlFor="title">Title *</label>
        <input
          type="text"
          id="title"
          name="title"
          value={formData.title}
          onChange={handleChange}
          required
          style={formStyles.input}
          placeholder="Enter quest title"
        />
      </div>

      <div style={formStyles.field}>
        <label style={formStyles.label} htmlFor="description">Description * <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '0.85rem' }}>(shown on card)</span></label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          required
          style={{ ...formStyles.textarea, minHeight: '110px' }}
          placeholder="Brief description shown on the quest card"
        />
      </div>

      <div style={formStyles.field}>
        <label style={formStyles.label} htmlFor="full_description">Full Description <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '0.85rem' }}>(shown in expanded view)</span></label>
        <textarea
          id="full_description"
          name="full_description"
          value={formData.full_description}
          onChange={handleChange}
          style={{ ...formStyles.textarea, minHeight: 'clamp(200px, 36vh, 560px)' }}
          placeholder="Detailed quest objectives, background, and hints shown when clicking on the quest card"
        />
      </div>

      <div style={formStyles.field}>
        <label style={formStyles.label}>
          Objectives / Steps <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '0.85rem' }}>(toggle each step's state later to reveal progress)</span>
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          {formData.steps.length === 0 && (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: 0 }}>No steps yet. Add objectives the party will work through.</p>
          )}
          {formData.steps.map((step, i) => (
            <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                type="text"
                value={step.text}
                onChange={(e) => updateStep(i, { text: e.target.value })}
                style={{ ...formStyles.input, flex: 1 }}
                placeholder={`Objective ${i + 1}`}
              />
              <select
                value={step.status}
                onChange={(e) => updateStep(i, { status: e.target.value })}
                style={{ ...formStyles.select, width: 'auto' }}
              >
                <option value="hidden" style={formStyles.option}>Hidden</option>
                <option value="active" style={formStyles.option}>Active</option>
                <option value="done" style={formStyles.option}>Done</option>
              </select>
              <button type="button" onClick={() => moveStep(i, -1)} title="Move up" style={{ ...formStyles.input, width: 'auto', cursor: 'pointer', padding: '0.5rem 0.6rem' }}>↑</button>
              <button type="button" onClick={() => moveStep(i, 1)} title="Move down" style={{ ...formStyles.input, width: 'auto', cursor: 'pointer', padding: '0.5rem 0.6rem' }}>↓</button>
              <button type="button" onClick={() => removeStep(i)} title="Remove" style={{ background: 'linear-gradient(135deg, #a93226 0%, #c0392b 100%)', color: '#fff', border: '1px solid #e74c3c', borderRadius: '6px', padding: '0.5rem 0.7rem', cursor: 'pointer' }}>✕</button>
            </div>
          ))}
          <button
            type="button"
            onClick={addStep}
            style={{ alignSelf: 'flex-start', background: 'linear-gradient(135deg, #1a5a6e 0%, #2a7a8e 100%)', color: '#fff', border: '1px solid var(--accent-teal)', borderRadius: '6px', padding: '0.5rem 1rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}
          >
            + Add Step
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
        <div>
          <label style={formStyles.label} htmlFor="quest_type">Quest Type</label>
          <select
            id="quest_type"
            name="quest_type"
            value={formData.quest_type}
            onChange={handleChange}
            style={formStyles.select}
          >
            <option value="main" style={formStyles.option}>Main Quest</option>
            <option value="side" style={formStyles.option}>Side Quest</option>
            <option value="optional" style={formStyles.option}>Optional</option>
          </select>
        </div>

        <div>
          <label style={formStyles.label} htmlFor="priority">Priority</label>
          <select
            id="priority"
            name="priority"
            value={formData.priority}
            onChange={handleChange}
            style={formStyles.select}
          >
            <option value="low" style={formStyles.option}>Low</option>
            <option value="normal" style={formStyles.option}>Normal</option>
            <option value="high" style={formStyles.option}>High</option>
          </select>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
        <label style={formStyles.checkbox}>
          <input
            type="checkbox"
            name="completed"
            checked={formData.completed === 1}
            onChange={handleChange}
            style={formStyles.checkboxInput}
          />
          <span style={{ color: 'var(--text-primary)', fontSize: '0.95rem' }}>
            Quest completed
          </span>
        </label>

        <label style={formStyles.checkbox}>
          <input
            type="checkbox"
            name="is_visible"
            checked={formData.is_visible === 1}
            onChange={handleChange}
            style={formStyles.checkboxInput}
          />
          <span style={{ color: 'var(--text-primary)', fontSize: '0.95rem' }}>
            Visible to players
          </span>
        </label>
      </div>
    </FormModal>
  )
}

export default QuestForm
